import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { customers } from '../services/api';
import LayoutV2 from '../components/LayoutV2';

function getDefaultDateRange() {
  const now = new Date();
  const toDate = now.toISOString().slice(0, 10);
  now.setDate(now.getDate() - 30);
  const fromDate = now.toISOString().slice(0, 10);
  return { fromDate, toDate };
}

export default function CustomersAdminV2() {
  const { isAdmin, isOperations } = useAuth();
  const [customersList, setCustomersList] = useState([]);
  const [loading, setLoading] = useState(true);
  const defaults = getDefaultDateRange();
  const [searchParams, setSearchParams] = useState({
    promoterKeyword: '',
    mobile: '',
    name: '',
    fromDate: defaults.fromDate,
    toDate: defaults.toDate
  });
  const [hasSearched, setHasSearched] = useState(true);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0
  });

  const loadCustomers = async (pageNum = pagination.page) => {
    setLoading(true);
    try {
      const params = {
        execute: true,
        page: pageNum,
        limit: pagination.limit,
        promoterKeyword: searchParams.promoterKeyword,
        mobile: searchParams.mobile,
        name: searchParams.name,
        fromDate: searchParams.fromDate,
        toDate: searchParams.toDate
      };
      Object.keys(params).forEach(key => {
        if (!params[key]) delete params[key];
      });

      const res = await customers.adminSearch(params);
      setCustomersList(res.data.customers || []);
      setPagination(prev => ({
        ...prev,
        page: pageNum,
        total: res.data.total || 0,
        totalPages: res.data.totalPages || 0
      }));
    } catch (err) {
      console.error('Failed to load customers:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (hasSearched) {
      loadCustomers(pagination.page);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.page]);

  useEffect(() => {
    loadCustomers(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    setHasSearched(true);
    if (pagination.page !== 1) {
      setPagination(prev => ({ ...prev, page: 1 }));
      return;
    }
    loadCustomers(1);
  };

  const handleInputChange = (e) => {
    setSearchParams(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const getStatusBadgeClass = (statusCode) => {
    const map = {
      closed_deal: 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/40',
      rejected: 'bg-rose-500/20 text-rose-300 border border-rose-500/40',
      applied: 'bg-cyan-500/20 text-cyan-300 border border-cyan-500/40',
      kyc_submitted: 'bg-indigo-500/20 text-indigo-300 border border-indigo-500/40',
      registered: 'bg-slate-500/20 text-slate-200 border border-slate-500/40',
      invalid: 'bg-rose-500/20 text-rose-300 border border-rose-500/40',
      unregistered: 'bg-amber-500/20 text-amber-300 border border-amber-500/40'
    };
    return map[statusCode] || map.registered;
  };

  // Redirect if not admin or operations
  if (!isAdmin && !isOperations) {
    return (
      <LayoutV2>
        <div className="text-red-400">Access denied. Admin only.</div>
      </LayoutV2>
    );
  }

  return (
    <LayoutV2>
      <div>
        <h1 className="text-lg font-semibold text-white mb-4">Customer Management</h1>

        {/* Search Form */}
        <form onSubmit={handleSearch} className="mb-4 flex flex-wrap gap-2 items-end">
          <div>
            <label className="text-xs text-slate-400 block mb-1">Promoter Name / Username</label>
            <input
              type="text"
              name="promoterKeyword"
              value={searchParams.promoterKeyword}
              onChange={handleInputChange}
              placeholder="Fuzzy match promoter"
              className="px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white text-sm w-64"
            />
          </div>
          <div>
            <label className="text-xs text-slate-400 block mb-1">Mobile</label>
            <input
              type="text"
              name="mobile"
              value={searchParams.mobile}
              onChange={handleInputChange}
              placeholder="Fuzzy search by phone"
              className="px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white text-sm w-32"
            />
          </div>
          <div>
            <label className="text-xs text-slate-400 block mb-1">Customer Name</label>
            <input
              type="text"
              name="name"
              value={searchParams.name}
              onChange={handleInputChange}
              placeholder="Fuzzy search by name"
              className="px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white text-sm w-44"
            />
          </div>
          <div>
            <label className="text-xs text-slate-400 block mb-1">From Date</label>
            <input
              type="date"
              name="fromDate"
              value={searchParams.fromDate}
              onChange={handleInputChange}
              className="px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white text-sm"
            />
          </div>
          <div>
            <label className="text-xs text-slate-400 block mb-1">To Date</label>
            <input
              type="date"
              name="toDate"
              value={searchParams.toDate}
              onChange={handleInputChange}
              className="px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white text-sm"
            />
          </div>
          <button
            type="submit"
            className="px-4 py-2 bg-teal-600 hover:bg-teal-700 rounded-lg text-white text-sm"
          >
            Search
          </button>
        </form>

        {/* Results */}
        {loading ? (
          <div className="text-slate-400">Loading customers...</div>
        ) : !hasSearched ? (
          <div className="text-slate-400">Set filters and click Search to load customers.</div>
        ) : customersList.length === 0 ? (
          <div className="text-slate-400">No customers found</div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-800 text-slate-400">
                  <tr>
                    <th className="px-3 py-2 text-left">Name</th>
                    <th className="px-3 py-2 text-left">Mobile</th>
                    <th className="px-3 py-2 text-left">Invite Code</th>
                    <th className="px-3 py-2 text-left">Status</th>
                    <th className="px-3 py-2 text-left">Register Time</th>
                    <th className="px-3 py-2 text-left">Actions</th>
                  </tr>
                </thead>
                <tbody className="text-white">
                  {customersList.map((customer) => (
                    <tr key={customer.id} className="border-t border-slate-700 hover:bg-slate-800">
                      <td className="px-3 py-2">{customer.user_name || 'N/A'}</td>
                      <td className="px-3 py-2 font-mono">{customer.mobile}</td>
                      <td className="px-3 py-2 font-mono text-teal-300">{customer.invite_code || 'N/A'}</td>
                      <td className="px-3 py-2">
                        <span className={`inline-flex px-2 py-1 rounded text-xs ${getStatusBadgeClass(customer.customerStatus)}`}>
                          {customer.customerStatusLabel || '--'}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-slate-400">
                        {(customer.register_time || customer.create_time)
                          ? new Date(customer.register_time || customer.create_time).toLocaleString()
                          : 'N/A'}
                      </td>
                      <td className="px-3 py-2">
                        <Link
                          to={`/admin/customers/${customer.mobile}`}
                          className="text-teal-400 hover:text-teal-300"
                        >
                          View Details →
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <div className="flex justify-center gap-2 mt-4">
                <button
                  onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                  disabled={pagination.page === 1}
                  className="px-3 py-1 bg-slate-700 rounded text-white text-sm disabled:opacity-50"
                >
                  Previous
                </button>
                <span className="px-3 py-1 text-slate-400 text-sm">
                  Page {pagination.page} of {pagination.totalPages}
                </span>
                <button
                  onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                  disabled={pagination.page >= pagination.totalPages}
                  className="px-3 py-1 bg-slate-700 rounded text-white text-sm disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            )}

            <div className="text-slate-400 text-sm mt-2">
              Total: {pagination.total} customers
            </div>
          </>
        )}
      </div>
    </LayoutV2>
  );
}
